import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Form, Input, Spin } from 'antd';
import { debounce } from 'ts-debounce';
import { action } from 'mobx';
import { ReactComponent as SelectArrowIcon } from '../../../icons/select-arrow-icon.svg';
import { Address } from '../../../schema';
import styles from './PrequalAddressAutoComplete.module.scss';
import PrequalPlacesAttribution from './PrequalPlacesAttribution';
import { STATES_EXCLUDED_FROM_FUNNELS } from '../../../constants';

type Props = {
  // current value
  onSelect: (address: Address) => void;
  restrictions?: any;
  // allow rest props for Input
  [x: string]: any;
};

const PrequalAddressAutocomplete: React.FC<Props> = ({
  onSelect,
  restrictions,
  validator,
  ...rest
}) => {
  const [fetching, setFetching] = useState(false);
  const [prequalOptions, setPrequalOptions] = useState<any>([]);
  const [sessionToken, setSessionToken] = useState(
    new google.maps.places.AutocompleteSessionToken()
  );

  const fetchRef = useRef(0);
  const [prequalAutocompleteService, setPrequalAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);

  const inputRest = {
    autoComplete: 'on',
    disabled: rest.disabled,
  };

  useEffect(() => {
    // @ts-ignore
    window.mapsPromise.then(() => {
      setPrequalAutocompleteService(new window.google.maps.places.AutocompleteService());
    });
  }, [setPrequalAutocompleteService]);

  const updatePrequalOptions = (predictions: any[]) => {
    const filteredPredictions = predictions.filter((p) => {
      const state = p.description.split(',').slice(-2, -1)[0]?.trim() || '';
      const isStateAllowed = !STATES_EXCLUDED_FROM_FUNNELS.has(state?.toLowerCase() || '');

      return isStateAllowed;
    });

    setPrequalOptions(
      filteredPredictions.map((p) => ({
        label: p.description,
        value: p.place_id,
      }))
    );
  };

  const prequalDebounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      if (!prequalAutocompleteService) return;
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setPrequalOptions([]);
      setFetching(true);

      prequalAutocompleteService.getPlacePredictions(
        {
          input: value,
          ...restrictions,
          sessionToken,
        },
        (predictions, status) => {
          setFetching(false);
          if (fetchId !== fetchRef.current) {
            return;
          }
          if (!predictions) return;
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            console.error('got error status %O', status);
            return;
          }
          updatePrequalOptions(predictions);
        }
      );
    };

    return debounce(loadOptions, 200);
  }, [sessionToken, prequalAutocompleteService, restrictions]);

  const reducedAddressComponents = (result: any) => {
    const results = result.address_components.reduce(
      // @ts-ignore
      (seed: any, { short_name, long_name, types }) => (
        // @ts-ignore
        // eslint-disable-next-line no-sequences
        types.forEach((t) => (seed[t] = short_name || long_name)), seed
      ),
      {}
    );
    return results;
  };

  const prequalSelectPlace = (placeId: string) => {
    // @ts-ignore
    const prequalPlaces: PlacesService = new window.google.maps.places.PlacesService(
      // @ts-ignore
      document.getElementById('prequal-places-attribution')
    );
    prequalPlaces.getDetails(
      { placeId, fields: ['address_components'], sessionToken },
      action((result: any, status: any) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          console.error(
            'auto complete on prequal request failed %O with status %O',
            result,
            status
          );
        }

        setSessionToken(new google.maps.places.AutocompleteSessionToken());
        // @ts-ignore
        const selected = reducedAddressComponents(result);

        onSelect({
          address1: [selected.street_number, selected.route].join(' '),
          country: selected.country,
          city: selected.locality || selected.neighborhood || selected.postal_town,
          state: selected.administrative_area_level_1,
          county: selected.administrative_area_level_2,
          zip: selected.postal_code,
        });
      })
    );
  };

  const handleSearch = (value: string) => {
    if (prequalAutocompleteService === null) return;
    // noinspection JSIgnoredPromiseFromCall
    prequalDebounceFetcher(value);
  };

  return (
    <>
      <div className={styles.labelContainer}>
        <div className={styles.labelText}>Address</div>
      </div>
      <div className={styles.addressSelect}>
        <Form.Item {...rest} rules={[{ validator }]}>
          <AutoComplete
            suffixIcon={<SelectArrowIcon />}
            showSearch
            allowClear
            placeholder="Enter Address"
            filterOption={false}
            onSearch={handleSearch}
            onSelect={prequalSelectPlace}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            options={prequalOptions}
            {...inputRest}
          />
        </Form.Item>
        <PrequalPlacesAttribution />
      </div>
    </>
  );
};

const PrequalAddressWrap: React.FC<Props> = (props) => {
  if (
    window.google?.maps?.places?.AutocompleteSessionToken &&
    window.google?.maps?.places?.AutocompleteService
  ) {
    return <PrequalAddressAutocomplete {...props} />;
  }

  return (
    <Form.Item {...props}>
      <Input disabled={props.disabled} />
    </Form.Item>
  );
};

export default PrequalAddressWrap;
